// src/context/RestaurantContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import restaurantService from '../services/restaurantService';
import { useDispatch } from 'react-redux';
import { setSelectedRestaurantId } from '../store/pos/branchSlice';
import { AuthContext } from './AuthContext';

export const RestaurantContext = createContext();

export const RestaurantProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { currentUser } = useContext(AuthContext);
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const selectRestaurant = useCallback((restaurant) => {
    setSelectedRestaurant(restaurant);
    if (restaurant) {
      localStorage.setItem('selectedRestaurant', JSON.stringify(restaurant));
      dispatch(setSelectedRestaurantId(restaurant._id || restaurant.id));
    } else {
      localStorage.removeItem('selectedRestaurant');
      dispatch(setSelectedRestaurantId(null));
    }
  }, [dispatch]);

  const fetchRestaurants = useCallback(async () => {
    if (!currentUser) {
      setRestaurants([]);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await restaurantService.getUserRestaurants();
      setRestaurants(data);
      
      // If user has only one restaurant, auto-select it
      if (data.length === 1 && !selectedRestaurant) {
        selectRestaurant(data[0]);
      }
    } catch (err) {
      setError(err.message || 'Error fetching restaurants');
      console.error(err);
      // Server unreachable (Ayder pattern: router up so navigator.onLine is
      // still true, but the WAN is down → ERR_NETWORK). Without this fallback
      // selectedRestaurant stays null — the restore effect below only runs
      // after the server list loads — and BranchContext then wipes the
      // restored branch selection, leaving the POS a blank screen. Restore
      // the last-used restaurant from localStorage so the offline caches
      // downstream (branches, posDataCache) stay reachable.
      if (!selectedRestaurant) {
        try {
          const saved = localStorage.getItem('selectedRestaurant');
          const parsed = saved ? JSON.parse(saved) : null;
          if (parsed?._id) {
            console.warn('[app] restaurants fetch failed — restoring cached restaurant', { id: parsed._id });
            setRestaurants(prev => (prev.length > 0 ? prev : [parsed]));
            setSelectedRestaurant(parsed);
            dispatch(setSelectedRestaurantId(parsed._id || parsed.id));
          }
        } catch (_) { /* corrupt cache — leave state as-is */ }
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, selectedRestaurant, selectRestaurant, dispatch]);
  
  // Initialize data when component mounts or user changes
  useEffect(() => {
    if (currentUser) {
      fetchRestaurants();
    } else {
      setRestaurants([]);
      setSelectedRestaurant(null);
    }
  }, [currentUser, fetchRestaurants]);
  
  // Load selected restaurant from localStorage after restaurants are fetched
  useEffect(() => {
    if (restaurants.length > 0 && currentUser && !selectedRestaurant) {
      const savedRestaurant = localStorage.getItem('selectedRestaurant');
      if (savedRestaurant) {
        try {
          const parsedRestaurant = JSON.parse(savedRestaurant);
          // Verify the saved restaurant is still accessible to the user
          const restaurantExists = restaurants.find(r => r._id === parsedRestaurant._id);
          if (restaurantExists) {
            setSelectedRestaurant(restaurantExists);
            dispatch(setSelectedRestaurantId(restaurantExists._id || restaurantExists.id));
          } else {
            localStorage.removeItem('selectedRestaurant');
          }
        } catch (error) {
          console.error('Error parsing saved restaurant:', error);
          localStorage.removeItem('selectedRestaurant');
        }
      }
    }
  }, [restaurants, currentUser, selectedRestaurant, dispatch]);

  // Clear selected restaurant if it's no longer accessible
  useEffect(() => {
    if (selectedRestaurant && restaurants.length > 0) {
      const isStillAccessible = restaurants.some(r => r._id === selectedRestaurant._id);
      if (!isStillAccessible) {
        setSelectedRestaurant(null);
        localStorage.removeItem('selectedRestaurant');
        dispatch(setSelectedRestaurantId(null));
      }
    }
  }, [restaurants, selectedRestaurant, dispatch]);
  
  // Create a new restaurant
  const createRestaurant = async (restaurantData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const newRestaurant = await restaurantService.createRestaurant(restaurantData);
      setRestaurants([...restaurants, newRestaurant]);
      return newRestaurant;
    } catch (err) {
      setError(err.message || 'Error creating restaurant');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Update a restaurant
  const updateRestaurant = async (id, restaurantData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const updatedRestaurant = await restaurantService.updateRestaurant(id, restaurantData);
      
      setRestaurants(restaurants.map(restaurant => 
        restaurant.id === id ? updatedRestaurant : restaurant
      ));
      
      return updatedRestaurant;
    } catch (err) {
      setError(err.message || 'Error updating restaurant');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Delete a restaurant
  const deleteRestaurant = async (id) => {
    setIsLoading(true);
    setError(null);
    
    try {
      await restaurantService.deleteRestaurant(id);
      setRestaurants(restaurants.filter(restaurant => restaurant.id !== id));
    } catch (err) {
      setError(err.message || 'Error deleting restaurant');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Get a single restaurant by ID
  const getRestaurantById = async (id) => {
    // First check if we already have it in state
    const existingRestaurant = restaurants.find(r => r.id === parseInt(id));
    
    if (existingRestaurant) {
      return existingRestaurant;
    }
    
    // Otherwise fetch from API
    setIsLoading(true);
    setError(null);
    
    try {
      const restaurant = await restaurantService.getRestaurantById(id);
      return restaurant;
    } catch (err) {
      setError(err.message || 'Error fetching restaurant');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };
  
  // Reset error state
  const clearError = () => {
    setError(null);
  };


  // Clear selected restaurant
  const clearSelectedRestaurant = () => {
    setSelectedRestaurant(null);
    localStorage.removeItem('selectedRestaurant');
  };
  
  // Context value
  const value = {
    restaurants,
    selectedRestaurant,
    isLoading,
    error,
    fetchRestaurants,
    createRestaurant,
    updateRestaurant,
    deleteRestaurant,
    getRestaurantById,
    selectRestaurant,
    clearSelectedRestaurant,
    clearError,
    refreshRestaurants: fetchRestaurants
  };
  
  return (
    <RestaurantContext.Provider value={value}>
      {children}
    </RestaurantContext.Provider>
  );
};

export default RestaurantProvider;